import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSettingsCache, useCacheMutations } from '@/shared/cache'
import { useApi } from '@/shared/utils/request'
import { toast } from 'sonner'
import type { NumericInput } from './SettingsShared'
import { GeneralSettings } from './sections/GeneralSettings'
import { TrackingSettings } from './sections/TrackingSettings'
import { NarrativesSettings } from './sections/NarrativesSettings'
import { AbbreviationsSettings } from './sections/AbbreviationsSettings'
import { RestrictedWordsSettings } from './sections/RestrictedWordsSettings'

const NAV_ITEMS = [
  { label: 'General', segment: '' },
  { label: 'Tracking', segment: 'tracking' },
  { label: 'Narratives', segment: 'narratives' },
  { label: 'Abbreviations', segment: 'abbreviations' },
  { label: 'Restricted Words', segment: 'restricted-words' },
] as const

type NavSegment = (typeof NAV_ITEMS)[number]['segment']

export default function SettingsPanel() {
  const { settings, isLoading: loading } = useSettingsCache()
  const { optimisticUpdate } = useCacheMutations()
  const { makeApiRequest } = useApi()
  const debounceRef = useRef<number | null>(null)
  const pendingPatchRef = useRef<Partial<typeof settings>>({})
  const settingsRef = useRef(settings)
  useEffect(() => { settingsRef.current = settings }, [settings])
  useEffect(() => () => { if (debounceRef.current) window.clearTimeout(debounceRef.current) }, [])

  const location = useLocation()
  const navigate = useNavigate()
  const segment = (location.pathname.split('/').filter(Boolean)[1] ?? '') as NavSegment
  const activeSegment = NAV_ITEMS.some((n) => n.segment === segment) ? segment : ''

  const navigateTo = (seg: NavSegment) =>
    navigate(seg === '' ? '/settings' : `/settings/${seg}`, { replace: true })

  const update = (patch: Partial<typeof settings>) => {
    optimisticUpdate('settings', (prev) => ({ ...prev, ...patch }))
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch }
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(async () => {
      const merged = pendingPatchRef.current
      pendingPatchRef.current = {}
      try {
        await makeApiRequest({
          endpoint: '/settings',
          method: 'POST',
          body: { ...settingsRef.current, ...merged },
        })
      } catch {
        toast.error('Failed to save settings')
      }
    }, 100)
  }

  const clamp = (raw: string, min: number, max: number, fallback: number) => {
    const n = parseInt(raw, 10)
    return isNaN(n) ? fallback : Math.min(max, Math.max(min, n))
  }

  const [emailTimeInput, setEmailTimeInput] = useState(String(settings.time_to_track_emails))
  const [docTimeInput, setDocTimeInput] = useState(String(settings.time_to_track_docs))
  const [wordCountInput, setWordCountInput] = useState(String(settings.word_count || 0))
  const [websiteTimeInput, setWebsiteTimeInput] = useState(String(settings.p_time_to_track_on_browser_s))
  const [inactivityInput, setInactivityInput] = useState(String(settings.p_browser_session_inactivity_threshold_m))
  const [meetingTimeInput, setMeetingTimeInput] = useState(String(settings.p_time_to_track_meetings_s))

  useEffect(() => {
    if (!loading) {
      setEmailTimeInput(String(settings.time_to_track_emails))
      setDocTimeInput(String(settings.time_to_track_docs))
      setWebsiteTimeInput(String(settings.p_time_to_track_on_browser_s))
      setWordCountInput(String(settings.word_count || 0))
      setInactivityInput(String(settings.p_browser_session_inactivity_threshold_m))
      setMeetingTimeInput(String(settings.p_time_to_track_meetings_s))
    }
  }, [loading]) // eslint-disable-line react-hooks/exhaustive-deps

  const emailTime: NumericInput = {
    value: emailTimeInput,
    onChange: setEmailTimeInput,
    onBlur: () => { const v = clamp(emailTimeInput, 5, 300, 20); setEmailTimeInput(String(v)); update({ time_to_track_emails: v }) },
    onStep: (d) => { const v = Math.min(300, Math.max(5, (parseInt(emailTimeInput, 10) || 5) + d)); setEmailTimeInput(String(v)); update({ time_to_track_emails: v }) },
  }

  const docTime: NumericInput = {
    value: docTimeInput,
    onChange: setDocTimeInput,
    onBlur: () => { const v = clamp(docTimeInput, 5, 300, 30); setDocTimeInput(String(v)); update({ time_to_track_docs: v }) },
    onStep: (d) => { const v = Math.min(300, Math.max(5, (parseInt(docTimeInput, 10) || 5) + d)); setDocTimeInput(String(v)); update({ time_to_track_docs: v }) },
  }

  const websiteTime: NumericInput = {
    value: websiteTimeInput,
    onChange: setWebsiteTimeInput,
    onBlur: () => { const v = clamp(websiteTimeInput, 5, 300, 20); setWebsiteTimeInput(String(v)); update({ p_time_to_track_on_browser_s: v }) },
    onStep: (d) => { const v = Math.min(300, Math.max(5, (parseInt(websiteTimeInput, 10) || 5) + d)); setWebsiteTimeInput(String(v)); update({ p_time_to_track_on_browser_s: v }) },
  }

  const inactivityThreshold: NumericInput = {
    value: inactivityInput,
    onChange: setInactivityInput,
    onBlur: () => { const v = clamp(inactivityInput, 5, 720, 30); setInactivityInput(String(v)); update({ p_browser_session_inactivity_threshold_m: v }) },
    onStep: (d) => { const v = Math.min(720, Math.max(5, (parseInt(inactivityInput, 10) || 30) + d)); setInactivityInput(String(v)); update({ p_browser_session_inactivity_threshold_m: v }) },
  }

  const meetingTime: NumericInput = {
    value: meetingTimeInput,
    onChange: setMeetingTimeInput,
    onBlur: () => { const v = clamp(meetingTimeInput, 5, 3600, 60); setMeetingTimeInput(String(v)); update({ p_time_to_track_meetings_s: v }) },
    onStep: (d) => { const v = Math.min(3600, Math.max(5, (parseInt(meetingTimeInput, 10) || 60) + d)); setMeetingTimeInput(String(v)); update({ p_time_to_track_meetings_s: v }) },
  }

  const wordCount: NumericInput = {
    value: wordCountInput,
    onChange: setWordCountInput,
    onBlur: () => { const v = clamp(wordCountInput, 5, 500, 30); setWordCountInput(String(v)); update({ word_count: v }) },
    onStep: (d) => { const v = Math.min(500, Math.max(5, (parseInt(wordCountInput, 10) || 5) + d)); setWordCountInput(String(v)); update({ word_count: v }) },
  }

  if (loading) {
    // Groups mirror the Tracking section: Narratives, General, Emails, Documents, Browser, Meetings
    const skeletonGroups = [
      { labelW: 72,  rows: [{ ctrl: 'toggle', labelW: 148, descW: 260 }] },
      { labelW: 56,  rows: [{ ctrl: 'toggle', labelW: 164, descW: 240 }, { ctrl: 'toggle', labelW: 200, descW: 280 }] },
      { labelW: 48,  rows: [{ ctrl: 'step',   labelW: 172, descW: 300 }, { ctrl: 'toggle', labelW: 188, descW: 260 }] },
      { labelW: 64,  rows: [{ ctrl: 'step',   labelW: 164, descW: 280 }, { ctrl: 'toggle', labelW: 192, descW: 240 }] },
      { labelW: 52,  rows: [{ ctrl: 'step',   labelW: 160, descW: 260 }, { ctrl: 'step',   labelW: 216, descW: 300 }] },
      { labelW: 60,  rows: [{ ctrl: 'step',   labelW: 184, descW: 240 }] },
    ]
    return (
      <div className="w-full h-full overflow-y-scroll font-sans animate-pulse">
        <div className="mx-auto max-w-[1100px] px-6 pt-20 pb-12">
          <div className="h-10 w-36 rounded bg-muted mb-6" />
          <div className="flex gap-8">
            {/* Nav skeleton — 5 full-width items, first one active */}
            <div className="w-48 shrink-0 flex flex-col gap-1">
              {[1, 0, 0, 0, 0].map((active, i) => (
                <div
                  key={i}
                  className={`h-8 w-full rounded-md ${active ? 'bg-accent' : 'bg-muted/50'}`}
                />
              ))}
            </div>
            {/* Content skeleton */}
            <div className="flex-1 min-w-0">
              {skeletonGroups.map((group, gi) => (
                <div key={gi} className="mb-6">
                  <div className="h-2.5 rounded bg-muted mb-2" style={{ width: group.labelW }} />
                  {group.rows.map((row, ri) => (
                    <div key={ri} className="flex items-center justify-between py-5 border-b border-border/40 last:border-b-0">
                      <div className="flex flex-col gap-1.5">
                        <div className="h-3.5 rounded bg-muted" style={{ width: row.labelW }} />
                        <div className="h-2.5 rounded bg-muted/60" style={{ width: row.descW }} />
                      </div>
                      {row.ctrl === 'toggle' ? (
                        <div className="h-5 w-9 rounded-full bg-muted shrink-0" />
                      ) : (
                        <div className="flex gap-1 shrink-0">
                          <div className="h-8 w-20 rounded bg-muted" />
                          <div className="flex flex-col gap-0.5">
                            <div className="h-[18px] w-7 rounded bg-muted" />
                            <div className="h-[18px] w-7 rounded bg-muted" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full overflow-y-scroll font-sans">
      <div className="mx-auto max-w-[1100px] px-6 pt-20 pb-12">
        <h1
          className="text-4xl font-semibold text-foreground/80 mb-6"
          style={{ fontFamily: '"Inter", sans-serif' }}
        >
          Settings
        </h1>
        <div className="flex gap-8">
          <nav className="w-48 shrink-0 sticky top-6 self-start">
            {NAV_ITEMS.map(({ label, segment: seg }) => (
              <button
                key={seg}
                type="button"
                onClick={() => navigateTo(seg)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium font-sans transition-colors ${
                  activeSegment === seg
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>

          {/* Right content — max-width 724px, scrolls with the page */}
          <div className="flex-1 min-w-0">
            {activeSegment === '' && (
              <GeneralSettings settings={settings} update={update} />
            )}
            {activeSegment === 'tracking' && (
              <TrackingSettings
                settings={settings}
                update={update}
                emailTime={emailTime}
                docTime={docTime}
                websiteTime={websiteTime}
                inactivityThreshold={inactivityThreshold}
                meetingTime={meetingTime}
              />
            )}
            {activeSegment === 'narratives' && (
              <NarrativesSettings
                settings={settings}
                update={update}
                wordCount={wordCount}
              />
            )}
            {activeSegment === 'abbreviations' && <AbbreviationsSettings />}
            {activeSegment === 'restricted-words' && <RestrictedWordsSettings />}
          </div>
        </div>
      </div>
    </div>
  )
}
